import { Alert, Badge, Card, EmptyState, Modal, Spinner, type Tone } from '@cartly/ui';
import { Zap } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { formatDateTime, formatRelative } from '../../app/format';
import { useLoader } from '../../app/hooks';
import { NODE_INFO, outcomeLabel, TRIGGER_LABEL } from './labels';
import type { NodeType } from './graph';
import type { FlowTrigger } from '@cartly/shared';

interface RunRow {
  id: string;
  status: 'running' | 'waiting' | 'completed' | 'failed' | 'cancelled';
  triggerType: string;
  outcome: string | null;
  error: string | null;
  currentNodeId: string | null;
  resumeAt: string | null;
  startedAt: string;
  completedAt: string | null;
}
interface RunDetail extends RunRow {
  steps: { nodeId: string; type: string; status: string; detail: string | null; at: string }[];
}

const TONE: Record<RunRow['status'], Tone> = {
  running: 'info',
  waiting: 'warning',
  completed: 'success',
  failed: 'critical',
  cancelled: 'neutral',
};
const STEP_TONE: Record<string, Tone> = {
  ok: 'success',
  waiting: 'neutral',
  skipped: 'warning',
  failed: 'critical',
};

const triggerTitle = (t: string) => TRIGGER_LABEL[t as FlowTrigger]?.title ?? t;

function RunModal({
  flowId,
  runId,
  onClose,
}: {
  flowId: string;
  runId: string;
  onClose: () => void;
}) {
  const detail = useLoader(() => api.get<RunDetail>(`/flows/${flowId}/runs/${runId}`), runId);
  return (
    <Modal open title="Run details" onClose={onClose}>
      {detail.loading && !detail.data ? (
        <Spinner label="Loading" />
      ) : detail.error || !detail.data ? (
        <Alert tone="critical">{detail.error ?? 'Failed to load'}</Alert>
      ) : (
        <div className="stack">
          <div className="row">
            <Badge tone={TONE[detail.data.status]}>{detail.data.status}</Badge>
            <span>{outcomeLabel(detail.data.outcome, detail.data.status)}</span>
          </div>
          <p className="text-muted text-sm">
            {triggerTitle(detail.data.triggerType)} · started{' '}
            {formatDateTime(detail.data.startedAt)}
            {detail.data.completedAt ? ` · ended ${formatDateTime(detail.data.completedAt)}` : ''}
          </p>
          {detail.data.error && <Alert tone="critical">{detail.data.error}</Alert>}
          {detail.data.steps.length === 0 ? (
            <p className="text-muted">No step has run yet.</p>
          ) : (
            <ol className="step-list">
              {detail.data.steps.map((s) => (
                <li key={s.nodeId} className="step-list__item">
                  <div>
                    <div className="table__primary">
                      {NODE_INFO[s.type as NodeType]?.title ?? s.type}{' '}
                      <span className="mono text-muted">{s.nodeId}</span>
                    </div>
                    {s.detail && <div className="table__secondary">{s.detail}</div>}
                    <div className="table__secondary">{formatDateTime(s.at)}</div>
                  </div>
                  <Badge tone={STEP_TONE[s.status] ?? 'neutral'}>{s.status}</Badge>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </Modal>
  );
}

/** Execution log of one flow: the latest 50 runs and what each step did. */
export function RunsPanel({ flowId, tenantId }: { flowId: string; tenantId: string | undefined }) {
  const runs = useLoader(
    () => api.get<{ runs: RunRow[] }>(`/flows/${flowId}/runs`).then((r) => r.runs),
    `${tenantId}:${flowId}`,
  );
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card
      flush
      title="Runs"
      subtitle="Each run is one customer going through the flow. The latest 50 are shown."
    >
      {runs.loading && !runs.data ? (
        <Spinner label="Loading" />
      ) : runs.error ? (
        <Alert tone="critical">{errorMessage(new Error(runs.error))}</Alert>
      ) : !runs.data || runs.data.length === 0 ? (
        <EmptyState icon={<Zap size={22} />} title="No runs yet">
          Runs appear here once the flow is published and a matching event arrives.
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map((r) => (
                <tr key={r.id}>
                  <td>
                    <button type="button" className="link-button" onClick={() => setOpen(r.id)}>
                      {formatRelative(r.startedAt)}
                    </button>
                  </td>
                  <td>{triggerTitle(r.triggerType)}</td>
                  <td>
                    <Badge tone={TONE[r.status]}>{r.status}</Badge>
                  </td>
                  <td>
                    {outcomeLabel(r.outcome, r.status)}
                    {r.status === 'waiting' && r.resumeAt && (
                      <div className="table__secondary">continues {formatRelative(r.resumeAt)}</div>
                    )}
                    {r.error && <div className="table__secondary">{r.error}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <RunModal flowId={flowId} runId={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}
